import type { SettingsFacetFieldSchema } from '@iconik-io/iconik-api/src/services/settings';

export type FieldType =
  | 'thumbnail'
  | 'title'
  | 'media_icon'
  | 'media_icon_with_status'
  | 'string'
  | 'string_exact'
  | 'text'
  | 'boolean'
  | 'float'
  | 'url'
  | 'integer'
  | 'approval'
  | 'analyze'
  | 'user'
  | 'storage'
  | 'date'
  | 'datetime'
  | 'drop_down'
  | 'tag_cloud'
  | 'status'
  | 'expand'
  | 'menu'
  | 'title_with_version'
  | 'email'
  | 'file_size'
  | 'custom_component'
  | 'custom_component_with_props';

export type NonCustomFieldType = Exclude<
  FieldType,
  'custom_component' | 'custom_component_with_props'
>;

export const FIELD_TYPES = {
  THUMBNAIL: 'thumbnail',
  TITLE: 'title',
  TITLE_WITH_VERSION: 'title_with_version',
  MEDIA_ICON: 'media_icon',
  MEDIA_ICON_WITH_STATUS: 'media_icon_with_status',
  STRING: 'string',
  STRINGEXACT: 'string_exact',
  TEXT: 'text',
  BOOLEAN: 'boolean',
  FLOAT: 'float',
  URL: 'url',
  INTEGER: 'integer',
  APPROVAL: 'approval',
  ANALYZE: 'analyze',
  USER: 'user',
  STORAGE: 'storage',
  DATE: 'date',
  DATETIME: 'datetime',
  DROP_DOWN: 'drop_down',
  TAG_CLOUD: 'tag_cloud',
  STATUS: 'status',
  CUSTOM_COMPONENT: 'custom_component',
  CUSTOM_COMPONENT_WITH_PROPS: 'custom_component_with_props',
  OPENER: 'expand',
  MENU: 'menu',
  EMAIL: 'email',
  FILE_SIZE: 'file_size',
} as const;

export const FIELD_KEYFRAMES = 'keyframes';
export const FIELD_TITLE = 'title';
export const FIELD_MEDIA_TYPE = 'media_type';
export const FIELD_OBJECT_TYPE = 'object_type';
export const FIELD_TYPE = 'type';
export const FIELD_APPROVAL = 'approval';
export const FIELD_APPROVAL_STATUS = 'approval_status';
export const FIELD_ARCHIVE_STATUS = 'archive_status';
export const FIELD_ANALYZE = 'analyze_status';
export const FIELD_CREATED_BY = 'created_by_user';
export const FIELD_STORED_IN = 'files.storage_id';
export const FIELD_DATE_CREATED = 'date_created';
export const FIELD_DATE_MODIFIED = 'date_modified';
export const FIELD_FORMAT = 'format';
export const FIELD_FORMATS_NAME = 'formats.name';
export const FIELD_ONLINE_STATUS = 'is_online';
export const FIELD_FILE_SIZE = 'files.size';
export const FIELD_DURATION = 'duration';

export type StatusLabelObject = {
  label: string;
  bsStyle: 'info' | 'danger' | 'warning';
};

export const STATUS_MAP: {
  [field: string]: {
    [status: string]: StatusLabelObject;
  };
} = {
  [FIELD_ARCHIVE_STATUS]: {
    ARCHIVING: { label: 'Archiving', bsStyle: 'info' },
    FAILED_TO_ARCHIVE: { label: 'Failed to Archive', bsStyle: 'danger' },
    ARCHIVED: { label: 'Archived', bsStyle: 'warning' },
  },
};

export type ColumnWidthDefinition = {
  min?: number;
  max?: number;
  fixed?: number;
  current?: number;
  autoResize?: boolean;
};

export type SearchField = {
  name: string;
  type: NonCustomFieldType;
  label: string;
  // Facetable is used when we convert fields from metadata fields to connect logic
  // that some metadata field should be shown as a facet filter.
  // Despite its name for non-metadata fields it doesn't reflect whether it will be shown in facet filters or not.
  facetable?: boolean;
  width?: ColumnWidthDefinition;
  sortable?: boolean;
  // Viewable fields have viewable values in search results and could be shown
  // as a separate column. By default, true. When explicitly set as false, the field can still be used
  // as a facet filter field, but will not appear in columns selector.
  viewable?: boolean;
  // Origin field is a data field that is behind some complex field.
  // For example `approval` is a structure which contains details about
  // `approval_status`. `approval_status` string value is used for sorting
  // or facets filtering while `approval` object is used for approval
  // value representation in a table view.
  originFieldName?: string;
};

const SORT_CONTROL_WIDTH = 16;
const FAVORITES_CONTROL_WIDTH = 28;

export const SEARCH_SYSTEM_FIELDS: SearchField[] = [
  {
    name: FIELD_KEYFRAMES,
    type: FIELD_TYPES.THUMBNAIL,
    label: 'Thumbnail',
    width: {
      fixed: 94,
    },
  },
  {
    name: FIELD_TITLE,
    type: FIELD_TYPES.TITLE,
    label: 'Title',
    sortable: true,
    width: {
      min: 100 + FAVORITES_CONTROL_WIDTH,
      autoResize: true,
    },
  },
  {
    name: FIELD_APPROVAL,
    type: FIELD_TYPES.APPROVAL,
    label: 'Approval',
    sortable: true,
    originFieldName: FIELD_APPROVAL_STATUS,
    width: {
      min: 82 + SORT_CONTROL_WIDTH,
      current: 82 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_TYPE,
    type: FIELD_TYPES.STRING,
    label: 'Type',
    sortable: true,
  },
  {
    name: FIELD_OBJECT_TYPE,
    type: FIELD_TYPES.STRING,
    label: 'Object type',
    sortable: true,
  },
  {
    name: FIELD_ONLINE_STATUS,
    type: FIELD_TYPES.STRING,
    label: 'Online status',
    sortable: true,
  },
  {
    name: FIELD_FORMAT,
    type: FIELD_TYPES.STRING,
    label: 'Format',
    sortable: true,
  },
  {
    name: FIELD_FORMATS_NAME,
    type: FIELD_TYPES.STRING,
    label: 'Format Name',
    viewable: false,
  },
  {
    name: FIELD_ARCHIVE_STATUS,
    type: FIELD_TYPES.STATUS,
    label: 'Archive',
    sortable: true,
    width: {
      min: 90,
      current: 90,
    },
  },
  {
    name: FIELD_ANALYZE,
    type: FIELD_TYPES.ANALYZE,
    label: 'Analyzed',
    sortable: true,
    width: {
      min: 80 + SORT_CONTROL_WIDTH,
      current: 80 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_CREATED_BY,
    type: FIELD_TYPES.USER,
    label: 'Created by',
    width: {
      min: 90,
      current: 90,
    },
  },
  {
    name: FIELD_STORED_IN,
    type: FIELD_TYPES.STORAGE,
    label: 'Storage',
    width: {
      min: 90,
      current: 90,
    },
  },
  {
    name: FIELD_FILE_SIZE,
    type: FIELD_TYPES.FILE_SIZE,
    label: 'File Size',
    sortable: true,
    width: {
      min: 75,
      current: 75,
    },
  },
  {
    name: FIELD_DATE_CREATED,
    type: FIELD_TYPES.DATE,
    label: 'Date created',
    sortable: true,
    width: {
      min: 105 + SORT_CONTROL_WIDTH,
      current: 105 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_DATE_MODIFIED,
    type: FIELD_TYPES.DATE,
    label: 'Date modified',
    sortable: true,
    width: {
      min: 105 + SORT_CONTROL_WIDTH,
      current: 105 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_MEDIA_TYPE,
    type: FIELD_TYPES.MEDIA_ICON_WITH_STATUS,
    label: 'Media Type',
    sortable: true,
    width: {
      min: 130 + SORT_CONTROL_WIDTH,
      current: 130 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_DURATION,
    type: FIELD_TYPES.STRING,
    label: 'Duration',
    sortable: true,
  },
];

export const REVIEW_SORT_FIELDS = [
  {
    name: FIELD_TITLE,
    type: FIELD_TYPES.TITLE,
    label: 'Title',
    sortable: true,
    width: {
      min: 100 + FAVORITES_CONTROL_WIDTH,
      autoResize: true,
    },
  },
  {
    name: FIELD_APPROVAL,
    type: FIELD_TYPES.APPROVAL,
    label: 'Approval',
    sortable: true,
    originFieldName: FIELD_APPROVAL_STATUS,
    width: {
      min: 82 + SORT_CONTROL_WIDTH,
      current: 82 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_MEDIA_TYPE,
    type: FIELD_TYPES.MEDIA_ICON_WITH_STATUS,
    label: 'Media Type',
    sortable: true,
    width: {
      min: 130 + SORT_CONTROL_WIDTH,
      current: 130 + SORT_CONTROL_WIDTH,
    },
  },
  {
    name: FIELD_FILE_SIZE,
    type: FIELD_TYPES.FILE_SIZE,
    label: 'File Size',
    sortable: true,
    width: {
      min: 75,
      current: 75,
    },
  },
  {
    name: FIELD_DATE_CREATED,
    type: FIELD_TYPES.DATE,
    label: 'Date created',
    sortable: true,
    width: {
      min: 105 + SORT_CONTROL_WIDTH,
      current: 105 + SORT_CONTROL_WIDTH,
    },
  },
];

export const SEARCH_SYSTEM_FIELDS_BY_NAME = Object.fromEntries(
  SEARCH_SYSTEM_FIELDS.map((item) => [item.name, item]),
);
export const SEARCH_CUSTOM_FILTER_DATE_MODIFIED = 'filter.date_modified';
export const SEARCH_CUSTOM_FILTER_DATE_CREATED = 'filter.date_created';
export const SEARCH_CUSTOM_FILTER_CREATED_BY = 'filter.created_by';
export const SEARCH_CUSTOM_FILTER_CREATED_BY_USER = 'filter.created_by_user';
export const SEARCH_CUSTOM_FILTER_METADATA_VALUES = 'filter.metadata_values';
export const SEARCH_CUSTOM_FILTER_TRANSCRIPTION_TEXT =
  'filter.transcription_text';
export const SEARCH_CUSTOM_FILTER_STORED_IN = 'filter.files.storage_id';
export const SEARCH_CUSTOM_FILTER_CONFIRMED_PERSONS = 'filter.person_ids';

export const SEARCH_CUSTOM_FILTERS: SearchField[] = [
  {
    name: SEARCH_CUSTOM_FILTER_DATE_CREATED,
    type: FIELD_TYPES.DATE,
    label: 'Date created',
  },
  {
    name: SEARCH_CUSTOM_FILTER_DATE_MODIFIED,
    type: FIELD_TYPES.DATE,
    label: 'Date modified',
  },
  {
    name: SEARCH_CUSTOM_FILTER_CREATED_BY,
    type: FIELD_TYPES.USER,
    label: 'Created by',
  },
  {
    name: SEARCH_CUSTOM_FILTER_METADATA_VALUES,
    type: FIELD_TYPES.STRING,
    label: 'Metadata values',
  },
  {
    name: SEARCH_CUSTOM_FILTER_STORED_IN,
    type: FIELD_TYPES.STORAGE,
    label: 'Storage',
  },
  {
    name: SEARCH_CUSTOM_FILTER_TRANSCRIPTION_TEXT,
    type: FIELD_TYPES.STRING,
    label: 'Transcription Text',
  },
  {
    name: SEARCH_CUSTOM_FILTER_CONFIRMED_PERSONS,
    type: FIELD_TYPES.STRING,
    label: 'Confirmed Persons',
  },
];

export const SEARCH_FACETABLE_FIELDS = [
  SEARCH_CUSTOM_FILTER_DATE_CREATED,
  SEARCH_CUSTOM_FILTER_CREATED_BY,
  SEARCH_CUSTOM_FILTER_STORED_IN,
  SEARCH_CUSTOM_FILTER_TRANSCRIPTION_TEXT,
  SEARCH_CUSTOM_FILTER_METADATA_VALUES,
  FIELD_APPROVAL_STATUS,
  FIELD_ARCHIVE_STATUS,
  FIELD_TYPE,
  FIELD_OBJECT_TYPE,
  FIELD_ONLINE_STATUS,
  FIELD_FORMAT,
  FIELD_FORMATS_NAME,
  FIELD_MEDIA_TYPE,
];

export const SEARCH_DISPLAY_FIELDS = [
  FIELD_KEYFRAMES,
  FIELD_TITLE,
  FIELD_DATE_CREATED,
  FIELD_DATE_MODIFIED,
  FIELD_ARCHIVE_STATUS,
  FIELD_CREATED_BY,
  FIELD_STORED_IN,
  FIELD_APPROVAL,
  FIELD_ANALYZE,
  FIELD_MEDIA_TYPE,
  FIELD_FILE_SIZE,
  FIELD_DURATION,
];

export const DEFAULT_FACET_FIELDS: SettingsFacetFieldSchema[] = [
  ...SEARCH_FACETABLE_FIELDS.map((field) => ({ name: field })),
  { name: SEARCH_CUSTOM_FILTER_CONFIRMED_PERSONS },
];

export const DEFAULT_DISPLAY_FIELDS = SEARCH_SYSTEM_FIELDS.filter((field) =>
  SEARCH_DISPLAY_FIELDS.includes(field.name),
);
